import { routeNames } from 'src/modules/core/constants/routeNames'

const pendingConnectedAppStorageKey = 'pendingConnectedAppAuthorize'
const pendingConnectedAppMaxAgeMs = 10 * 60 * 1000

export type PendingConnectedAppAuthorize = {
  state: string
  isBookingUser: boolean
  /**
   * Acquisition channel from the handoff URL (`shopify`, `wordpress`, ...).
   *
   * Persisted alongside the state because the authorize path is rebuilt from
   * this record after a session restore, re-entry, or onboarding bounce. It was
   * previously dropped, so the tag was often already gone by the time the
   * backend saw the signup.
   */
  source?: string
  createdAt: string
}

export function savePendingConnectedAppAuthorize(payload: PendingConnectedAppAuthorize): void {
  sessionStorage.setItem(pendingConnectedAppStorageKey, JSON.stringify(payload))
}

export function getPendingConnectedAppAuthorize(): PendingConnectedAppAuthorize | null {
  const raw = sessionStorage.getItem(pendingConnectedAppStorageKey)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as PendingConnectedAppAuthorize
    if (!parsed?.state || !parsed?.createdAt) {
      clearPendingConnectedAppAuthorize()
      return null
    }

    const createdAtMs = new Date(parsed.createdAt).getTime()
    if (Number.isNaN(createdAtMs) || Date.now() - createdAtMs > pendingConnectedAppMaxAgeMs) {
      clearPendingConnectedAppAuthorize()
      return null
    }

    return parsed
  } catch {
    clearPendingConnectedAppAuthorize()
    return null
  }
}

export function getPendingConnectedAppAuthorizePath(): string | null {
  const pending = getPendingConnectedAppAuthorize()
  if (!pending) {
    return null
  }

  const params = new URLSearchParams({
    state: pending.state,
  })

  if (pending.isBookingUser) {
    params.set('isBookingUser', 'true')
  }

  if (pending.source) {
    params.set('source', pending.source)
  }

  return `${routeNames.connectedAppAuthorize.path}?${params.toString()}`
}

export function clearPendingConnectedAppAuthorize(): void {
  sessionStorage.removeItem(pendingConnectedAppStorageKey)
}
